import React, { useEffect, useMemo, useState } from "react";
import { Save, MessageCircle, ExternalLink, Search, Globe2, Clock, Building2, Share2, MousePointerClick, PanelBottom } from "lucide-react";
import { toast } from "sonner";
import api, { notifyError, formatApiError, imageUrl } from "@/lib/api";
import PlatformLayout from "@/components/PlatformLayout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { UploadButton } from "@/pages/platform/PlatformSettings";

const EMPTY = {
  contact: { whatsapp_number: "", whatsapp_default_message: "", contact_email: "", short_address: "" },
  seo: {
    site_title: "", meta_description: "", keywords: [], canonical_base_url: "",
    og_image_url: "", twitter_image_url: "",
    google_site_verification: "", bing_site_verification: "", facebook_domain_verification: "",
  },
  company: { company_name: "", company_address: "", company_tagline: "" },
  social: { facebook_url: "", instagram_url: "", tiktok_url: "", youtube_url: "", linkedin_url: "" },
  footer: { footer_text: "" },
  cta: { cta_get_started_url: "", cta_book_demo_url: "", cta_free_trial_url: "" },
};

const SECTIONS = Object.keys(EMPTY);

const mergeSettings = (data) =>
  Object.fromEntries(SECTIONS.map((s) => [s, { ...EMPTY[s], ...(data?.[s] || {}) }]));

const inputCls = "bg-white/10 border-white/10 text-white placeholder:text-white/30";

const COMING_SOON_TABS = [
  { key: "general", label: "General", desc: "Konten umum landing page (headline, deskripsi, CTA)." },
  { key: "hero", label: "Hero", desc: "Editor hero section landing page." },
  { key: "analytics", label: "Analytics", desc: "Google Analytics, GTM, dan pixel tracking." },
];

function ComingSoon({ tab }) {
  return (
    <Card className="bg-white/5 border-white/10 border-dashed p-10 rounded-2xl text-center" data-testid={`lp-coming-soon-${tab.key}`}>
      <Clock size={28} className="mx-auto text-white/30 mb-3" />
      <div className="flex items-center justify-center gap-2 mb-2">
        <h3 className="font-display font-bold text-white">{tab.label}</h3>
        <Badge className="bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 text-[10px]">Segera Hadir</Badge>
      </div>
      <p className="text-sm text-white/50 max-w-md mx-auto">{tab.desc} Bagian ini belum aktif pada rilis Go Live — struktur sudah disiapkan.</p>
    </Card>
  );
}

function SimpleFieldsTab({ section, title, icon: Icon, fields, values, upd, save, saving, note, children }) {
  return (
    <div className="space-y-4">
      <Card className="bg-white/5 border-white/10 p-5 rounded-2xl space-y-4">
        <h3 className="text-sm font-display font-bold text-white flex items-center gap-2">{Icon && <Icon size={14} />} {title}</h3>
        {note && <p className="text-[11px] text-white/40 -mt-2">{note}</p>}
        {fields.map((f) => (
          <div key={f.key}>
            <Label className="text-white/60 text-xs">{f.label}</Label>
            {f.textarea ? (
              <Textarea value={values[f.key] || ""} onChange={(e) => upd(section, f.key, e.target.value)} rows={2}
                maxLength={f.max} placeholder={f.placeholder} className={inputCls} data-testid={`lp-${section}-${f.key.replace(/_/g, "-")}`} />
            ) : (
              <Input value={values[f.key] || ""} onChange={(e) => upd(section, f.key, e.target.value)}
                maxLength={f.max} placeholder={f.placeholder} className={`${inputCls} ${f.mono ? "font-mono text-xs" : ""}`}
                data-testid={`lp-${section}-${f.key.replace(/_/g, "-")}`} />
            )}
            {f.help && <p className="text-[11px] text-white/40 mt-1">{f.help}</p>}
          </div>
        ))}
        {children}
      </Card>
      <Button onClick={() => save([section])} disabled={saving} className="brand-btn-primary rounded-xl h-11 px-6 font-bold" data-testid={`lp-${section}-save`}>
        <Save size={15} className="mr-2" /> {saving ? "Menyimpan..." : `Simpan ${title}`}
      </Button>
    </div>
  );
}

const COMPANY_FIELDS = [
  { key: "company_name", label: "Nama Perusahaan", placeholder: "Dynicty", max: 100 },
  { key: "company_tagline", label: "Tagline", placeholder: "Platform toko online all-in-one", max: 150 },
  { key: "company_address", label: "Alamat Perusahaan", placeholder: "Jakarta, Indonesia", max: 250, textarea: true },
];

const SOCIAL_FIELDS = [
  { key: "facebook_url", label: "Facebook URL", placeholder: "https://facebook.com/dynicty", mono: true },
  { key: "instagram_url", label: "Instagram URL", placeholder: "https://instagram.com/dynicty", mono: true },
  { key: "tiktok_url", label: "TikTok URL", placeholder: "https://tiktok.com/@dynicty", mono: true },
  { key: "youtube_url", label: "YouTube URL", placeholder: "https://youtube.com/@dynicty", mono: true },
  { key: "linkedin_url", label: "LinkedIn URL", placeholder: "https://linkedin.com/company/dynicty", mono: true },
];

const CTA_FIELDS = [
  { key: "cta_get_started_url", label: "Get Started URL", placeholder: "https://app.dynicty.com/register atau /register", mono: true },
  { key: "cta_book_demo_url", label: "Book Demo URL", placeholder: "https://calendly.com/dynicty/demo", mono: true },
  { key: "cta_free_trial_url", label: "Free Trial URL", placeholder: "https://app.dynicty.com/register?trial=1", mono: true },
];

const FOOTER_FIELDS = [
  { key: "footer_text", label: "Footer Text", placeholder: `© ${new Date().getFullYear()} Dynicty. All rights reserved.`, max: 200 },
];

const waLink = (num, msg) => (num ? `https://wa.me/${num}${msg ? `?text=${encodeURIComponent(msg)}` : ""}` : "");

function ContactTab({ contact, upd, save, saving }) {
  const link = waLink(contact.whatsapp_number, contact.whatsapp_default_message);
  return (
    <div className="space-y-4">
      <Card className="bg-white/5 border-white/10 p-5 rounded-2xl space-y-4">
        <h3 className="text-sm font-display font-bold text-white flex items-center gap-2"><MessageCircle size={14} /> Kontak Landing Page</h3>
        <div>
          <Label className="text-white/60 text-xs">Nomor WhatsApp</Label>
          <Input value={contact.whatsapp_number} onChange={(e) => upd("contact", "whatsapp_number", e.target.value.replace(/[^\d]/g, ""))}
            placeholder="6281234567890" className={inputCls} data-testid="lp-contact-wa-number" />
          <p className="text-[11px] text-white/40 mt-1">Format internasional tanpa + dan tanpa awalan 0. Contoh: 6281234567890</p>
        </div>
        <div>
          <Label className="text-white/60 text-xs">Pesan Default WhatsApp</Label>
          <Textarea value={contact.whatsapp_default_message} onChange={(e) => upd("contact", "whatsapp_default_message", e.target.value)}
            rows={2} maxLength={500} placeholder="Halo Dynicty, saya tertarik memiliki toko online sendiri."
            className={inputCls} data-testid="lp-contact-wa-message" />
          <p className="text-[11px] text-white/40 mt-1 text-right">{(contact.whatsapp_default_message || "").length}/500</p>
        </div>
        <div>
          <Label className="text-white/60 text-xs">Email Kontak (opsional)</Label>
          <Input type="email" value={contact.contact_email} onChange={(e) => upd("contact", "contact_email", e.target.value)}
            placeholder="halo@dynicty.com" className={inputCls} data-testid="lp-contact-email" />
        </div>
        <div>
          <Label className="text-white/60 text-xs">Alamat Singkat (opsional)</Label>
          <Input value={contact.short_address} onChange={(e) => upd("contact", "short_address", e.target.value)}
            maxLength={200} placeholder="Jakarta, Indonesia" className={inputCls} data-testid="lp-contact-address" />
        </div>
      </Card>

      <Card className="bg-white/5 border-white/10 p-5 rounded-2xl">
        <h3 className="text-sm font-display font-bold text-white mb-3">Preview Link WhatsApp</h3>
        {link ? (
          <div className="flex items-center gap-3 flex-wrap">
            <code className="text-xs text-cyan-300 bg-black/30 px-3 py-2 rounded-lg break-all flex-1 min-w-[200px]" data-testid="lp-wa-preview-link">{link}</code>
            <Button type="button" variant="outline" size="sm" onClick={() => window.open(link, "_blank", "noopener")}
              className="border-white/20 text-white hover:bg-white/10" data-testid="lp-wa-test-btn">
              <ExternalLink size={12} className="mr-1" /> Test WhatsApp Link
            </Button>
          </div>
        ) : (
          <p className="text-xs text-white/40" data-testid="lp-wa-preview-empty">Isi nomor WhatsApp untuk melihat preview link.</p>
        )}
      </Card>

      <Button onClick={() => save(["contact"])} disabled={saving} className="brand-btn-primary rounded-xl h-11 px-6 font-bold" data-testid="lp-contact-save">
        <Save size={15} className="mr-2" /> {saving ? "Menyimpan..." : "Simpan Contact"}
      </Button>
    </div>
  );
}

function SeoPreviews({ seo }) {
  const title = seo.site_title || "Dynicty — Platform Toko Online All-in-One";
  const desc = seo.meta_description || "Deskripsi meta akan tampil di sini.";
  const base = (seo.canonical_base_url || "https://dynicty.com").replace(/^https?:\/\//, "");
  const og = seo.og_image_url ? imageUrl(seo.og_image_url) : "";
  const tw = seo.twitter_image_url ? imageUrl(seo.twitter_image_url) : og;
  return (
    <Card className="bg-white/5 border-white/10 p-5 rounded-2xl space-y-5">
      <h3 className="text-sm font-display font-bold text-white flex items-center gap-2"><Search size={14} /> Preview (read-only, live dari form)</h3>

      <div data-testid="lp-preview-google">
        <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold mb-1.5">Google Search</div>
        <div className="bg-white rounded-xl p-4">
          <div className="text-xs text-[#202124] flex items-center gap-1"><span className="text-[#5f6368]">{base}</span></div>
          <div className="text-[#1a0dab] text-lg leading-snug truncate">{title}</div>
          <div className="text-sm text-[#4d5156] line-clamp-2">{desc}</div>
        </div>
      </div>

      <div data-testid="lp-preview-facebook">
        <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold mb-1.5">Facebook / WhatsApp (Open Graph)</div>
        <div className="bg-white rounded-xl overflow-hidden max-w-md">
          <div className="h-40 bg-[#e4e6eb] grid place-items-center overflow-hidden">
            {og ? <img src={og} alt="OG" className="w-full h-full object-cover" /> : <span className="text-xs text-[#65676b]">OG Image belum di-set</span>}
          </div>
          <div className="p-3 bg-[#f0f2f5]">
            <div className="text-[10px] uppercase text-[#65676b]">{base}</div>
            <div className="text-sm font-bold text-[#050505] truncate">{title}</div>
            <div className="text-xs text-[#65676b] line-clamp-1">{desc}</div>
          </div>
        </div>
      </div>

      <div data-testid="lp-preview-twitter">
        <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold mb-1.5">Twitter / X Card</div>
        <div className="bg-black border border-[#2f3336] rounded-2xl overflow-hidden max-w-md">
          <div className="h-40 bg-[#16181c] grid place-items-center overflow-hidden">
            {tw ? <img src={tw} alt="Twitter" className="w-full h-full object-cover" /> : <span className="text-xs text-[#71767b]">Twitter Image belum di-set</span>}
          </div>
          <div className="p-3">
            <div className="text-xs text-[#71767b]">{base}</div>
            <div className="text-sm font-bold text-white truncate">{title}</div>
            <div className="text-xs text-[#71767b] line-clamp-1">{desc}</div>
          </div>
        </div>
      </div>
    </Card>
  );
}

function SeoTab({ seo, upd, save, saving }) {
  const kwString = Array.isArray(seo.keywords) ? seo.keywords.join(", ") : (seo.keywords || "");
  const chips = kwString.split(",").map((k) => k.trim()).filter(Boolean);
  return (
    <div className="grid lg:grid-cols-2 gap-4 items-start">
      <div className="space-y-4">
        <Card className="bg-white/5 border-white/10 p-5 rounded-2xl space-y-4">
          <h3 className="text-sm font-display font-bold text-white flex items-center gap-2"><Globe2 size={14} /> SEO Landing Page</h3>
          <div>
            <Label className="text-white/60 text-xs">SEO Title (wajib)</Label>
            <Input value={seo.site_title} onChange={(e) => upd("seo", "site_title", e.target.value)} maxLength={120}
              placeholder="Dynicty — Platform Toko Online All-in-One" className={inputCls} data-testid="lp-seo-title" />
            <p className={`text-[11px] mt-1 text-right ${(seo.site_title || "").length > 70 ? "text-amber-400" : "text-white/40"}`}>{(seo.site_title || "").length}/70 disarankan</p>
          </div>
          <div>
            <Label className="text-white/60 text-xs">Meta Description (wajib)</Label>
            <Textarea value={seo.meta_description} onChange={(e) => upd("seo", "meta_description", e.target.value)} rows={3} maxLength={200}
              placeholder="Deskripsi singkat untuk hasil pencarian..." className={inputCls} data-testid="lp-seo-description" />
            <p className={`text-[11px] mt-1 text-right ${(seo.meta_description || "").length > 160 ? "text-amber-400" : "text-white/40"}`}>{(seo.meta_description || "").length}/160-200</p>
          </div>
          <div>
            <Label className="text-white/60 text-xs">Keywords (pisahkan dengan koma)</Label>
            <Input value={kwString} onChange={(e) => upd("seo", "keywords", e.target.value)}
              placeholder="toko online, saas, umkm" className={inputCls} data-testid="lp-seo-keywords" />
            {chips.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="lp-seo-keyword-chips">
                {chips.map((k, i) => <span key={`${k}-${i}`} className="text-[10px] bg-white/10 text-white/70 px-2 py-0.5 rounded-full">{k}</span>)}
              </div>
            )}
          </div>
          <div>
            <Label className="text-white/60 text-xs">Canonical Base URL</Label>
            <Input value={seo.canonical_base_url} onChange={(e) => upd("seo", "canonical_base_url", e.target.value)}
              placeholder="https://dynicty.com" className={`${inputCls} font-mono text-xs`} data-testid="lp-seo-canonical" />
            <p className="text-[11px] text-white/40 mt-1">Wajib HTTPS. Localhost/preview domain ditolak.</p>
          </div>
          <div>
            <Label className="text-white/60 text-xs">OG Image (Facebook/WhatsApp, 1200×630)</Label>
            <UploadButton label="OG Image" value={seo.og_image_url} onChange={(v) => upd("seo", "og_image_url", v)} testId="lp-seo-og-image" />
          </div>
          <div>
            <Label className="text-white/60 text-xs">Twitter Image</Label>
            <UploadButton label="Twitter Image" value={seo.twitter_image_url} onChange={(v) => upd("seo", "twitter_image_url", v)} testId="lp-seo-twitter-image" />
          </div>
        </Card>

        <Card className="bg-white/5 border-white/10 p-5 rounded-2xl space-y-4">
          <h3 className="text-sm font-display font-bold text-white">Site Verification</h3>
          <p className="text-[11px] text-white/40 -mt-2">Masukkan hanya nilai token (content), bukan tag meta lengkap. Untuk Google Domain Property, DNS TXT tetap direkomendasikan.</p>
          <div>
            <Label className="text-white/60 text-xs">Google Search Console</Label>
            <Input value={seo.google_site_verification} onChange={(e) => upd("seo", "google_site_verification", e.target.value)}
              placeholder="token verifikasi google" className={`${inputCls} font-mono text-xs`} data-testid="lp-seo-verif-google" />
          </div>
          <div>
            <Label className="text-white/60 text-xs">Bing Webmaster</Label>
            <Input value={seo.bing_site_verification} onChange={(e) => upd("seo", "bing_site_verification", e.target.value)}
              placeholder="token verifikasi bing" className={`${inputCls} font-mono text-xs`} data-testid="lp-seo-verif-bing" />
          </div>
          <div>
            <Label className="text-white/60 text-xs">Facebook / Meta Domain Verification</Label>
            <Input value={seo.facebook_domain_verification} onChange={(e) => upd("seo", "facebook_domain_verification", e.target.value)}
              placeholder="token verifikasi meta" className={`${inputCls} font-mono text-xs`} data-testid="lp-seo-verif-fb" />
          </div>
        </Card>

        <Button onClick={() => save(["seo"])} disabled={saving} className="brand-btn-primary rounded-xl h-11 px-6 font-bold" data-testid="lp-seo-save">
          <Save size={15} className="mr-2" /> {saving ? "Menyimpan..." : "Simpan SEO"}
        </Button>
      </div>

      <SeoPreviews seo={{ ...seo, keywords: chips }} />
    </div>
  );
}

export default function LandingPageSettingsPage() {
  const [s, setS] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("contact");
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    api.get("/platform/settings/landing-page")
      .then((r) => setS(mergeSettings(r.data)))
      .catch(notifyError)
      .finally(() => setLoading(false));
  }, []);

  // Unsaved changes guard — tutup/reload browser
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (dirty) { e.preventDefault(); e.returnValue = ""; }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  const upd = (section, k, v) => {
    setDirty(true);
    setS((x) => ({ ...x, [section]: { ...x[section], [k]: v } }));
  };

  const changeTab = (next) => {
    if (dirty && !window.confirm("Ada perubahan yang belum disimpan. Pindah tab dan buang perubahan?")) return;
    setTab(next);
  };

  const save = async (sections) => {
    setSaving(true);
    try {
      const payload = {};
      sections.forEach((sec) => { payload[sec] = s[sec]; });
      const r = await api.put("/platform/settings/landing-page", payload);
      setS(mergeSettings(r.data?.settings));
      setDirty(false);
      toast.success("Landing Page settings tersimpan — langsung berlaku di dynicty.com.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const comingSoonMap = useMemo(() => Object.fromEntries(COMING_SOON_TABS.map((t) => [t.key, t])), []);

  if (loading) return <PlatformLayout title="Landing Page Settings"><div className="text-white/60">Memuat...</div></PlatformLayout>;

  const activeTrigger = (v, label, testid) => (
    <TabsTrigger value={v} className="data-[state=active]:bg-white/10 text-white/60 data-[state=active]:text-white font-bold" data-testid={testid}>{label}</TabsTrigger>
  );

  return (
    <PlatformLayout title="Landing Page Settings">
      <div className="max-w-5xl" data-testid="landing-page-settings-page">
        <p className="text-sm text-white/50 -mt-4 mb-4">
          Kelola konten website marketing <span className="text-cyan-300 font-mono">dynicty.com</span> tanpa coding. Perubahan langsung berlaku setelah disimpan.
          {dirty && <span className="ml-2 text-amber-300 font-bold" data-testid="lp-unsaved-indicator">● Belum disimpan</span>}
        </p>
        <Tabs value={tab} onValueChange={changeTab}>
          <TabsList className="bg-white/5 border border-white/10 flex-wrap h-auto">
            {activeTrigger("contact", "Contact", "lp-tab-contact")}
            {activeTrigger("seo", "SEO", "lp-tab-seo")}
            {activeTrigger("company", "Company", "lp-tab-company")}
            {activeTrigger("social", "Social Media", "lp-tab-social")}
            {activeTrigger("cta", "CTA Buttons", "lp-tab-cta")}
            {activeTrigger("footer", "Footer", "lp-tab-footer")}
            {COMING_SOON_TABS.map((t) => (
              <TabsTrigger key={t.key} value={t.key} className="data-[state=active]:bg-white/10 text-white/60 data-[state=active]:text-white" data-testid={`lp-tab-${t.key}`}>
                {comingSoonMap[t.key].label} <span className="ml-1.5 text-[9px] text-cyan-300/80">●</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {COMING_SOON_TABS.map((t) => (
            <TabsContent key={t.key} value={t.key} className="mt-4"><ComingSoon tab={t} /></TabsContent>
          ))}
          <TabsContent value="contact" className="mt-4">
            <ContactTab contact={s.contact} upd={upd} save={save} saving={saving} />
          </TabsContent>
          <TabsContent value="seo" className="mt-4">
            <SeoTab seo={s.seo} upd={upd} save={save} saving={saving} />
          </TabsContent>
          <TabsContent value="company" className="mt-4">
            <SimpleFieldsTab section="company" title="Company Info" icon={Building2} fields={COMPANY_FIELDS}
              values={s.company} upd={upd} save={save} saving={saving}
              note="Identitas perusahaan yang tampil di landing page (header, about, footer)." />
          </TabsContent>
          <TabsContent value="social" className="mt-4">
            <SimpleFieldsTab section="social" title="Social Media" icon={Share2} fields={SOCIAL_FIELDS}
              values={s.social} upd={upd} save={save} saving={saving}
              note="Wajib HTTPS URL. Kosongkan untuk menyembunyikan icon di landing page (tidak ada broken link)." />
          </TabsContent>
          <TabsContent value="cta" className="mt-4">
            <SimpleFieldsTab section="cta" title="CTA Buttons" icon={MousePointerClick} fields={CTA_FIELDS}
              values={s.cta} upd={upd} save={save} saving={saving}
              note="Terima HTTPS URL penuh atau path relatif (mis. /register). Kosong = fallback perilaku default landing page." />
          </TabsContent>
          <TabsContent value="footer" className="mt-4">
            <SimpleFieldsTab section="footer" title="Footer" icon={PanelBottom} fields={FOOTER_FIELDS}
              values={s.footer} upd={upd} save={save} saving={saving}
              note="Kosong = fallback teks footer existing di landing page." />
          </TabsContent>
        </Tabs>
      </div>
    </PlatformLayout>
  );
}
